<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
import { RecapResponse } from '../../types/recap'
import { storeToRefs } from 'pinia'
import { useStoreRecaps } from '../../store/useStoreRecaps'
import RecapDefinition from '../../components/recaps/RecapDefinition.vue'
import { useStore } from '../../store'
import { useI18n } from 'vue-i18n'

onBeforeMount(async () => {
  //add store's recap definitions into memory
  await useStoreRecaps().setRecapsForStore(String(props.storeId))
})
const piniaStore = useStore()
const { t } = useI18n({ useScope: 'global' })

const props = defineProps<{ storeId: string }>()
//Store survey options
const recapStore = storeToRefs(useStoreRecaps())
const recapDefinition = ref<typeof RecapDefinition | null>(null)
const recaps = computed(() =>
  Object.keys(recapStore.recaps.value)
    .map((key) => recapStore.recaps.value[key])
    .filter((recap) => recap.store_id === (props.storeId as string))
)
const surveySelection = ref()
const storeSurveyOptions = ref([
  ...recaps.value.map((recap) => ({ label: recap.recapDefinition!.name, value: recap })),
])
const currentRecapResponse = computed(() =>
  recapStore.recap_responses.value.find(
    (res) =>
      res.events_recaps_id === surveySelection.value.value.recapDefinition.id &&
      res.store_id === (props.storeId as string)
  )
)
const recapSubmittedForCurrentRecap = computed(() => !!currentRecapResponse.value)
watch(recaps, () => {
  storeSurveyOptions.value = recaps.value.map((recap) => ({
    label: recap.recapDefinition!.name,
    value: recap,
  }))
})
function clearStoreRecapSelectionBeforeUpdate(value: any) {
  // Clears the selection of the store recap before performing the final update
  // This is to ensure the new recap loads and stale data isn't present
  // Also, manually clear the forms saved in the useFields component.
  // For some reason, they weren't being cleared properly when swapping between recaps,
  // which caused carryover data between responses
  surveySelection.value = null
  void nextTick(() => {
    ;(recapDefinition.value as any)?.resetRecap()
    void nextTick(() => {
      surveySelection.value = value
    })
  })
}

async function endSurveyAndFetchRecap() {
  if (surveySelection.value) {
    const res = await useStoreRecaps().saveRecap(
      recapDefinition.value,
      props.storeId as string,
      surveySelection.value.value.recapDefinition.id
    )
    if (res === true) {
      const recapSubmittedEvent = new CustomEvent<{ success: boolean }>('recapSubmitted', {
        detail: {
          success: true,
        },
      })
      window.dispatchEvent(recapSubmittedEvent)
    }
  }
}

async function editRecap() {
  if (currentRecapResponse.value) {
    await nextTick().catch(null)
    await nextTick().catch(null)
    const unmodifiedStore = useStoreRecaps()
    await unmodifiedStore.deleteRecapResponse(currentRecapResponse.value as RecapResponse)
  }
}

function refreshStoreRecapAFterSync() {
  if (recapSubmittedForCurrentRecap.value) {
    clearStoreRecapSelectionBeforeUpdate(null)
  }
}

onMounted(() => {
  window.addEventListener('syncStarted', refreshStoreRecapAFterSync)
})
onUnmounted(() => {
  window.removeEventListener('syncStarted', refreshStoreRecapAFterSync)
})
</script>
<template>
  <template v-if="storeSurveyOptions && storeSurveyOptions.length">
    <q-select
      filled
      v-model="surveySelection"
      @update:model-value="clearStoreRecapSelectionBeforeUpdate"
      :options="storeSurveyOptions"
      label="Select Survey"
      behavior="menu"
      clearable
    />
    <div v-if="surveySelection" class="q-py-md">
      <Suspense>
        <RecapDefinition
          ref="recapDefinition"
          :task-or-store-id="props.storeId"
          :recap-id="surveySelection.value.recapDefinition.id"
          recap-type="store"
        >
        </RecapDefinition>
        <template #fallback>
          <q-page class="full-width column items-center tw-my-6">
            {{ t('recap.loadingRecap', 0, { locale: piniaStore.user.language }) }}
          </q-page>
        </template>
      </Suspense>
      <q-btn
        v-if="recapSubmittedForCurrentRecap"
        class="tw--mt-64 q-mx-md"
        :color="'primary'"
        @click="editRecap"
      >
        {{ t('storeItems.update_survey', 0, { locale: piniaStore.user.language }) }}
      </q-btn>
      <q-btn v-else class="tw--mt-64 q-mx-md" @click="endSurveyAndFetchRecap">
        {{ t('storeItems.end_survey', 0, { locale: piniaStore.user.language }) }}
      </q-btn>
    </div>
  </template>
</template>
